import React, { useState } from 'react';
import { ChevronDown, CircleCheck } from 'lucide-react';
import { Pill } from '../../../components/ui';
import { ROLE_LABEL } from '../graphAnalytics';
import { AreaSummary, CONCENTRATION_HINT, Concentration, InsightBundle, ScoreFlag } from './influenceInsights';
import { InfluenceEntity } from './influenceModel';
import { areaColor, EntityAvatar, PanelSection, ShareBar } from './ui';

interface BriefingRailProps {
  bundle: InsightBundle;
  inScope: InfluenceEntity[];
  totalEntities: number;
  focusAreaId: string | null;
  selectedId: string | null;
  onFocusArea: (areaId: string) => void;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  /** The AI assessment block (BriefPanel) */
  assessment: React.ReactNode;
}

const CONCENTRATION_TONE: Record<Concentration, 'red' | 'amber' | 'neutral' | 'outline'> = {
  Dominated: 'amber',
  Concentrated: 'neutral',
  Competitive: 'outline',
  Fragmented: 'outline',
};

export const BriefingRail: React.FC<BriefingRailProps> = ({
  bundle,
  inScope,
  totalEntities,
  focusAreaId,
  selectedId,
  onFocusArea,
  onSelect,
  onHover,
  assessment,
}) => {
  const assessed = inScope.filter((e) => e.assessed);
  const people = inScope.filter((e) => e.type === 'stakeholder').length;
  const average = assessed.length ? Math.round(assessed.reduce((a, e) => a + e.average, 0) / assessed.length) : 0;
  const coverage = inScope.length ? Math.round((assessed.length / inScope.length) * 100) : 0;
  const { gaps } = bundle;
  const hubIds = new Set(gaps.unassessedHubs.map((u) => u.entity.id));
  const unassessedEdge = inScope.filter((e) => !e.assessed && !hubIds.has(e.id));

  return (
    <div className="space-y-7 px-4 pb-8 pt-4">
      {/* Headline */}
      <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-neutral-200">
        <Headline label="In view" value={inScope.length} hint={`${people} people · ${inScope.length - people} orgs`} title={`${inScope.length} of ${totalEntities} entities`} />
        <Headline label="Assessed" value={`${coverage}%`} hint={`${assessed.length} of ${inScope.length}`} className="border-l" />
        <Headline label="Avg influence" value={assessed.length ? average : '—'} hint={`${bundle.areas.length} areas`} className="border-t" />
        <Headline
          label="Gaps"
          value={gaps.total}
          hint={gaps.total ? 'to resolve' : 'none found'}
          className="border-l border-t"
          tone={gaps.total ? 'amber' : undefined}
        />
      </div>

      {/* Areas */}
      <PanelSection title="Power by area" aside={<span title="Share of influence weight (score²) held by the top actors">Share of top 5</span>}>
        <div className="divide-y divide-neutral-200/70 rounded-2xl border border-neutral-200">
          {bundle.areas.map((summary) => (
            <AreaCard
              key={summary.area.id}
              summary={summary}
              focused={focusAreaId === summary.area.id}
              selectedId={selectedId}
              onFocus={() => onFocusArea(summary.area.id)}
              onSelect={onSelect}
              onHover={onHover}
            />
          ))}
          {!bundle.areas.length && <p className="px-4 py-3 text-[12.5px] text-neutral-400">No areas in view</p>}
        </div>
      </PanelSection>

      {/* Cross-domain */}
      <PanelSection title="Cross-domain influencers" aside={<span title="Score 60+ in two or more areas">60+ in 2+ areas</span>}>
        {bundle.cross.length ? (
          <ul className="-mx-2">
            {bundle.cross.slice(0, 6).map(({ entity, strong }) => (
              <li key={entity.id}>
                <EntityRow entity={entity} selected={entity.id === selectedId} onSelect={onSelect} onHover={onHover}>
                  <span className="flex items-center gap-1">
                    {strong.slice(0, 4).map((s) => (
                      <span key={s.categoryId} className="flex items-center gap-1 text-[11.5px] tabular-nums text-neutral-500" title={`${s.label} ${s.score}`}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: areaColor(s.color) }} />
                        {s.score}
                      </span>
                    ))}
                  </span>
                </EntityRow>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[12.5px] leading-snug text-neutral-500">No actor holds strong influence in more than one area — influence is siloed.</p>
        )}
      </PanelSection>

      {/* Gaps */}
      <PanelSection title="Gaps">
        {gaps.total === 0 ? (
          <p className="flex items-center gap-2 text-[12.5px] text-neutral-500">
            <CircleCheck className="h-4 w-4 text-emerald-600" strokeWidth={1.75} />
            Coverage looks complete
          </p>
        ) : (
          <div className="divide-y divide-neutral-200/70 rounded-2xl border border-neutral-200">
            {gaps.unassessedHubs.length > 0 && (
              <GapGroup
                title="Unassessed network actors"
                hint="Central in the network but without influence scores — assess these first"
                count={gaps.unassessedHubs.length}
                defaultOpen
              >
                {gaps.unassessedHubs.slice(0, 6).map((u) => (
                  <EntityRow key={u.entity.id} entity={u.entity} selected={u.entity.id === selectedId} onSelect={onSelect} onHover={onHover}>
                    <Pill tone="outline">{ROLE_LABEL[u.role]}</Pill>
                  </EntityRow>
                ))}
              </GapGroup>
            )}
            {gaps.lowConfidence.length > 0 && (
              <GapGroup title="Low-confidence scores" hint="Confidence below 50%" count={gaps.lowConfidence.length}>
                <FlagRows flags={gaps.lowConfidence} selectedId={selectedId} onSelect={onSelect} onHover={onHover} detail={(f) => `${f.score.confidence}%`} />
              </GapGroup>
            )}
            {gaps.stale.length > 0 && (
              <GapGroup title="Stale scores" hint="Not updated in 12 months" count={gaps.stale.length}>
                <FlagRows flags={gaps.stale} selectedId={selectedId} onSelect={onSelect} onHover={onHover} detail={(f) => (f.score.updatedAt || '').slice(0, 7)} />
              </GapGroup>
            )}
            {gaps.thinAreas.length > 0 && (
              <GapGroup title="Thin areas" hint="Fewer than three scored actors" count={gaps.thinAreas.length}>
                <ul className="flex flex-wrap gap-1.5 px-3 pb-3">
                  {gaps.thinAreas.map((a) => (
                    <li key={a.id}>
                      <button
                        type="button"
                        onClick={() => onFocusArea(a.id)}
                        className="inline-flex h-6 items-center gap-1.5 rounded-full px-2 text-[12px] text-neutral-600 ring-1 ring-inset ring-neutral-200 hover:text-ink"
                      >
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: areaColor(a.color) }} />
                        {a.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </GapGroup>
            )}
            {gaps.unassessedTotal > gaps.unassessedHubs.length && (
              <GapGroup
                title={gaps.unassessedHubs.length ? 'Other unassessed' : 'Unassessed'}
                hint="No influence scores and few or no recorded ties"
                count={gaps.unassessedTotal - gaps.unassessedHubs.length}
              >
                {unassessedEdge.slice(0, 6).map((entity) => (
                  <EntityRow key={entity.id} entity={entity} selected={entity.id === selectedId} onSelect={onSelect} onHover={onHover} />
                ))}
                {unassessedEdge.length > 6 && <p className="px-2 pb-1 text-[11.5px] text-neutral-400">+{unassessedEdge.length - 6} more</p>}
              </GapGroup>
            )}
          </div>
        )}
      </PanelSection>

      <PanelSection title="Assessment">{assessment}</PanelSection>
    </div>
  );
};

const Headline: React.FC<{ label: string; value: React.ReactNode; hint?: string; title?: string; className?: string; tone?: 'amber' }> = ({
  label,
  value,
  hint,
  title,
  className = '',
  tone,
}) => (
  <div className={`min-w-0 border-neutral-200/70 px-3 py-2.5 ${className}`} title={title}>
    <p className="flex items-center gap-1.5 truncate text-[11.5px] text-neutral-500">
      {tone === 'amber' && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />}
      {label}
    </p>
    <p className="mt-1 text-[20px] leading-none tracking-[-0.02em] tabular-nums text-ink">{value}</p>
    {hint && <p className="mt-1 truncate text-[11px] text-neutral-400">{hint}</p>}
  </div>
);

const AreaCard: React.FC<{
  summary: AreaSummary;
  focused: boolean;
  selectedId: string | null;
  onFocus: () => void;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
}> = ({ summary, focused, selectedId, onFocus, onSelect, onHover }) => {
  const { area, leader } = summary;
  return (
    <div className={`relative px-3 py-2.5 transition-colors first:rounded-t-[15px] last:rounded-b-[15px] ${focused ? 'bg-neutral-50' : ''}`}>
      <button
        type="button"
        onClick={onFocus}
        aria-pressed={focused}
        title={`Focus ${area.label}`}
        className="flex w-full items-center gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
      >
        <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: areaColor(area.color) }} />
        <span className="min-w-0 flex-1 truncate text-[13px] text-ink hover:underline">{area.label}</span>
        {summary.scored > 0 && (
          <Pill tone={CONCENTRATION_TONE[summary.concentration]} title={`${CONCENTRATION_HINT[summary.concentration]} · HHI ${summary.hhi}`}>
            {summary.concentration}
          </Pill>
        )}
      </button>
      {leader ? (
        <>
          <button
            type="button"
            onClick={() => onSelect(leader.entity.id)}
            onMouseEnter={() => onHover(leader.entity.id)}
            onMouseLeave={() => onHover(null)}
            className={`-mx-1 mt-1.5 flex w-[calc(100%+0.5rem)] items-center gap-2 rounded-lg px-1 py-1 text-left transition-colors hover:bg-neutral-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
              leader.entity.id === selectedId ? 'bg-primary-50/60' : ''
            }`}
          >
            <EntityAvatar entity={leader.entity} size="xs" />
            <span className="min-w-0 flex-1 truncate text-[12.5px] text-neutral-700">{leader.entity.label}</span>
            <span className="text-[12.5px] tabular-nums text-ink">{leader.score}</span>
          </button>
          <ShareBar
            shares={summary.top.map((t) => ({ label: t.entity.label, share: t.share }))}
            color={area.color}
            className="mt-1.5"
          />
          <p className="mt-1.5 flex items-center justify-between text-[11.5px] tabular-nums text-neutral-400">
            <span>
              {summary.scored} scored · avg {summary.average}
            </span>
            <span title="Share of influence weight held by the leader">{Math.round(leader.share * 100)}% leader</span>
          </p>
        </>
      ) : (
        <p className="mt-1 text-[12px] text-neutral-400">No scored actors</p>
      )}
    </div>
  );
};

const EntityRow: React.FC<{
  entity: InfluenceEntity;
  selected: boolean;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  children?: React.ReactNode;
}> = ({ entity, selected, onSelect, onHover, children }) => (
  <button
    type="button"
    onClick={() => onSelect(entity.id)}
    onMouseEnter={() => onHover(entity.id)}
    onMouseLeave={() => onHover(null)}
    className={`flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
      selected ? 'bg-primary-50/60' : ''
    }`}
  >
    <EntityAvatar entity={entity} size="xs" />
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[13px] text-ink">{entity.label}</span>
      <span className="block truncate text-[11.5px] text-neutral-400">{entity.subtitle}</span>
    </span>
    {children}
  </button>
);

const GapGroup: React.FC<{ title: string; hint: string; count: number; defaultOpen?: boolean; children: React.ReactNode }> = ({
  title,
  hint,
  count,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={hint}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600/40"
      >
        <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{title}</span>
        <span className="text-[12px] tabular-nums text-neutral-400">{count}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.75} />
      </button>
      {open && <div className="px-1 pb-1.5 infl-rise">{children}</div>}
    </div>
  );
};

const FlagRows: React.FC<{
  flags: ScoreFlag[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  detail: (flag: ScoreFlag) => string;
}> = ({ flags, selectedId, onSelect, onHover, detail }) => (
  <>
    {flags.slice(0, 6).map((flag) => (
      <EntityRow key={`${flag.entity.id}-${flag.score.categoryId}`} entity={flag.entity} selected={flag.entity.id === selectedId} onSelect={onSelect} onHover={onHover}>
        <span className="flex items-center gap-1.5 text-[11.5px] tabular-nums text-neutral-500" title={flag.score.label}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: areaColor(flag.score.color) }} />
          {detail(flag)}
        </span>
      </EntityRow>
    ))}
    {flags.length > 6 && <p className="px-2 pb-1 text-[11.5px] text-neutral-400">+{flags.length - 6} more</p>}
  </>
);
